import { TodoInputBlock } from '@coast/schemas';
import { useCallback, useContext, useEffect, useState } from 'react';
import { NativeSyntheticEvent, TextInput, TextInputKeyPressEventData } from 'react-native';
import { View } from '@shared/components/View';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { CardRenderContext } from '@shared/components/blocks/contexts/render';
import { Fields, TodoItem } from '@shared/components/blocks/types';
import { MBIconV2 } from '@shared/components/image';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTypographyStyles } from '@shared/components/typography';
import { ColorThemeContext } from '@shared/styles';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import * as uuid from '@shared/util/uuid';
import { useScrollTextInputIntoView } from './hooks';

interface Props {
  block: TodoInputBlock;
  onAdd(todo: TodoItem[]): void;
  onBlur?(): void;
}

function createTodo(summary: string): TodoItem {
  return {
    summary,
    status: 'NEEDS-ACTION',
    id: uuid.v4(),
  };
}

function useAddUnsubmittedValueInPresubmitHook(value: string, block: TodoInputBlock) {
  const { registerPresubmitHook, unregisterPresubmitHook } = useCardFormContext();
  useEffect(() => {
    registerPresubmitHook(block.name, async (values: Fields) => {
      if (!value) {
        return;
      }
      return {
        [block.name]: [...(values[block.name] || []), createTodo(value)],
      };
    });
    return () => unregisterPresubmitHook(block.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, block.name]);
}

export function MBTodoAddRow({ block, onAdd, onBlur }: Props) {
  const colors = useContext(ColorThemeContext);
  const renderContext = useContext(CardRenderContext);
  const isDesktop = useIsDesktop();

  const [value, setValue] = useState('');
  useAddUnsubmittedValueInPresubmitHook(value, block);

  const scrollTextInputIntoView = useScrollTextInputIntoView();

  const handleAdd = useCallback(() => {
    if (!!value?.trim()) {
      onAdd([createTodo(value.trim())]);
      setTimeout(scrollTextInputIntoView);
    }

    // https://github.com/necolas/react-native-web/issues/1013#issuecomment-402548880
    setTimeout(() => setValue(''), 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onAdd, value]);

  // Inspired by:
  // https://github.com/necolas/react-native-web/blob/7ef070195bbdec4d776d1fae8f98914fe2574d58/packages/react-native-web/src/exports/TextInput/index.js#L281
  const handleNativeKeyPress = useCallback(
    (event: NativeSyntheticEvent<TextInputKeyPressEventData> & KeyboardEvent) => {
      if (event.nativeEvent.key === 'Enter' && !event.shiftKey) {
        event.preventDefault?.();
        handleAdd();
      }
    },
    [handleAdd],
  );

  const handlePaste = useCallback(
    (pastedValue: string): boolean => {
      const lines = pastedValue.split('\n').filter(line => !!line);
      if (lines.length === 1) {
        return true; // will not prevent paste event default
      } else if (lines.length >= 2) {
        onAdd(lines.map(createTodo));
        setValue('');
        scrollTextInputIntoView();
      }

      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onAdd],
  );

  return (
    <View row paddingHorizontal={3}>
      <MBTouchableOpacity disabled={!value} onPress={handleAdd}>
        <MBIconV2 name="plus-circle" color={colors.accent} />
      </MBTouchableOpacity>

      <View paddingRight={renderContext === 'form' ? 2 : 1} />
      {isDesktop && (
        <MBAutosizeTextInput
          autoFocus={block.autoFocus}
          blurOnSubmit={false}
          value={value}
          returnKeyType="done"
          padding="none"
          placeholder={block.placeholder || i18n('card.blocks.todoInput.addPlaceholder')}
          style={{ flex: 1, alignSelf: 'center', marginTop: 4, marginBottom: 4 }}
          color={colors.foregroundActive}
          onFocus={scrollTextInputIntoView}
          onChangeText={setValue}
          onPaste={handlePaste}
          onBlur={onBlur}
          onKeyPress={handleNativeKeyPress}
        />
      )}
      {!isDesktop && (
        <View flex style={{ alignSelf: 'center' }}>
          <TextInput
            autoFocus={block.autoFocus}
            blurOnSubmit={false}
            textAlignVertical={'center'}
            style={[MBTypographyStyles.body1, { paddingTop: 0, paddingBottom: 0 }]}
            value={value}
            returnKeyType="done"
            placeholder={block.placeholder || i18n('card.blocks.todoInput.addPlaceholder')}
            onFocus={scrollTextInputIntoView}
            onChangeText={setValue}
            onBlur={onBlur}
            multiline
            onSubmitEditing={() => {
              handleAdd();
            }}
          />
        </View>
      )}
    </View>
  );
}
